import { useState, useEffect, useRef, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Layout from '../components/Layout';
import api from '../services/api';
import { REGION_META } from '../data/team';

const hexFor = (r) => (REGION_META[r] && REGION_META[r].hex) || '#8A949A';
const titleCase = (s) =>
  String(s || '').toLowerCase().replace(/\b([a-z])/g, (m, c) => c.toUpperCase()).replace(/\b(Ev|Alta|Gis|Hr|Cad)\b/gi, (m) => m.toUpperCase());
const initials = (n) => {
  const p = String(n).trim().split(/\s+/);
  return ((p[0] || '')[0] || '') + ((p[p.length - 1] || '')[0] || '');
};
const cleanTel = (t) => String(t).replace(/[^0-9+]/g, '');

function RegionRow({ label, hex, count, active, onClick }) {
  return (
    <tr onClick={onClick} className="cursor-pointer border-b transition hover:bg-white/5" style={{ borderColor: '#13212f', background: active ? 'rgba(201,168,76,0.08)' : 'transparent' }}>
      <td className="px-4 py-3" style={{ boxShadow: active ? 'inset 3px 0 0 #c9a84c' : 'none' }}>
        <span className="inline-block align-middle mr-3 rounded" style={{ width: 12, height: 12, background: hex || 'linear-gradient(135deg,#2C6E8F,#C9962B,#4C7C4A,#B5532E)' }} />
        <span className="font-semibold text-gray-200">{label}</span>
      </td>
      <td className="px-4 py-3 text-right text-xs font-mono" style={{ color: active ? '#c9a84c' : '#8a949a', width: '1%', whiteSpace: 'nowrap' }}>{count}</td>
    </tr>
  );
}

function ContactRow({ label, value, href }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b" style={{ borderColor: '#13212f' }}>
      <div className="text-[10px] uppercase tracking-widest text-gray-500 w-24 shrink-0 pt-1 font-mono">{label}</div>
      <div className="flex-1 text-sm break-words">
        {value
          ? (href ? <a href={href} className="text-gold-400 hover:underline">{value}</a> : <span className="text-gray-200">{value}</span>)
          : <span className="text-gray-600 italic">not listed</span>}
      </div>
    </div>
  );
}

function ContactCard({ person: p, onClose }) {
  const hex = hexFor(p.region);
  const coord = p.lat != null ? `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}` : '—';
  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{ backgroundColor: '#142236', boxShadow: '0 24px 70px rgba(0,0,0,0.5)' }}>
        <div className="relative p-5 pr-14 text-white" style={{ background: hex }}>
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-2.5 right-2.5 z-10 w-10 h-10 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 transition text-white"
          >
            <span className="text-2xl leading-none">×</span>
          </button>
          <div className="text-[11px] uppercase tracking-widest opacity-90 font-mono">{titleCase(p.region)}</div>
          <div className="text-2xl font-black tracking-tight mt-1.5 leading-none">{p.name}</div>
          <div className="text-sm opacity-95 mt-1">{titleCase(p.title)}</div>
        </div>
        <div className="text-[11px] font-mono px-5 py-2 text-gray-400" style={{ backgroundColor: '#0b1622' }}>
          {p.office || '—'} &nbsp;·&nbsp; <span className="text-gold-400">{coord}</span>
        </div>
        <div className="px-5 py-2">
          <ContactRow label="Work" value={p.phone} href={p.phone ? `tel:${cleanTel(p.phone)}` : null} />
          <ContactRow label="Cell" value={p.cell} href={p.cell ? `tel:${cleanTel(p.cell)}` : null} />
          <ContactRow label="Email" value={p.email} href={p.email ? `mailto:${p.email}` : null} />
          <ContactRow label="Home address" value={p.home} />
        </div>
      </div>
    </div>
  );
}

export default function DirectoryPage() {
  // Live directory, mirrored from the BLVD HR app (the source of truth).
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState(null);
  const [title, setTitle] = useState('ALL');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markersRef = useRef({});
  const boundsRef = useRef(null);

  useEffect(() => {
    api.get('/directory')
      .then(({ data }) => setPeople(
        (data || []).map((p) => ({ ...p, lat: p.lat == null ? null : Number(p.lat), lng: p.lng == null ? null : Number(p.lng) }))
      ))
      .catch(() => setPeople([]))
      .finally(() => setLoading(false));
  }, []);

  // Build the Leaflet map once the directory has loaded.
  useEffect(() => {
    if (mapObj.current || !mapRef.current || !people.length) return;
    const map = L.map(mapRef.current, { scrollWheelZoom: false, zoomControl: true }).setView([39.5, -98.35], 4);
    // Esri Dark Gray Canvas — keyless (CARTO's free tiles now come back blurred with an
    // "API key required" watermark). Base + Reference (labels/borders); native tiles stop at z16.
    const ESRI_CANVAS = 'https://services.arcgisonline.com/ArcGIS/rest/services/Canvas';
    L.tileLayer(`${ESRI_CANVAS}/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}`, {
      attribution: 'Esri, HERE, Garmin, &copy; OpenStreetMap contributors', maxNativeZoom: 16, maxZoom: 19,
    }).addTo(map);
    L.tileLayer(`${ESRI_CANVAS}/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}`, {
      maxNativeZoom: 16, maxZoom: 19,
    }).addTo(map);
    people.forEach((p, i) => {
      if (p.lat == null || p.lng == null) return;
      const m = L.circleMarker([p.lat, p.lng], { radius: 7, color: '#0d1b2e', weight: 2, fillColor: hexFor(p.region), fillOpacity: 1 }).addTo(map);
      m.bindTooltip(`<b>${p.name}</b><br>${titleCase(p.title)}`, { direction: 'top', offset: [0, -4] });
      m.on('click', () => setSelected(p));
      markersRef.current[i] = m;
    });
    boundsRef.current = L.latLngBounds(people.filter((p) => p.lat != null).map((p) => [p.lat, p.lng]));
    mapObj.current = map;
    setTimeout(() => { map.invalidateSize(); if (boundsRef.current.isValid()) map.fitBounds(boundsRef.current, { padding: [40, 40], maxZoom: 5 }); }, 150);
    return () => { map.remove(); mapObj.current = null; markersRef.current = {}; };
  }, [people]);

  const filtered = useMemo(() => people.map((p, i) => ({ p, i })).filter(({ p }) => {
    if (region && p.region !== region) return false;
    if (title !== 'ALL' && p.title !== title) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [people, region, title, search]);

  // Emphasize matching markers, dim the rest while filtering
  useEffect(() => {
    const filtering = region || title !== 'ALL' || search;
    const matchIdx = new Set(filtered.map((o) => o.i));
    Object.entries(markersRef.current).forEach(([i, m]) => {
      const on = !filtering || matchIdx.has(+i);
      m.setStyle({ fillOpacity: on ? 1 : 0.18, opacity: on ? 1 : 0.3 });
      m.setRadius(on ? 7 : 5);
    });
  }, [filtered, region, title, search]);

  const regions = useMemo(() => {
    const present = [...new Set(people.map((p) => p.region))];
    return Object.keys(REGION_META).filter((r) => present.includes(r)).concat(present.filter((r) => !REGION_META[r]));
  }, [people]);

  const titles = useMemo(() => {
    const counts = {};
    people.forEach((p) => { counts[p.title] = (counts[p.title] || 0) + 1; });
    return { counts, list: Object.keys(counts).sort() };
  }, [people]);

  function selectRegion(key) {
    const next = region === key ? null : key;
    setRegion(next);
    const map = mapObj.current;
    if (!map) return;
    const b = next === null
      ? boundsRef.current
      : L.latLngBounds(people.filter((p) => p.region === next && p.lat != null).map((p) => [p.lat, p.lng]));
    if (b && b.isValid()) map.flyToBounds(b, { padding: [40, 40], maxZoom: 9, duration: 0.6 });
  }

  useEffect(() => {
    if (!selected) return;
    const i = people.indexOf(selected);
    const map = mapObj.current;
    const m = markersRef.current[i];
    if (selected.lat != null && map && m) {
      map.flyTo([selected.lat, selected.lng], Math.max(map.getZoom(), 6), { duration: 0.5 });
      m.openTooltip();
    }
  }, [selected, people]);

  function reset() {
    setRegion(null); setTitle('ALL'); setSearch('');
    const map = mapObj.current;
    if (map && boundsRef.current?.isValid()) map.flyToBounds(boundsRef.current, { padding: [40, 40], maxZoom: 5 });
  }

  const filtering = region || title !== 'ALL' || search;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-black text-white tracking-widest uppercase">Team Directory</h1>
            <p className="text-gray-400 text-sm mt-1">{loading ? 'Loading…' : `${people.length} team members`}</p>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name…"
            className="rounded px-3 py-2 text-sm text-white placeholder-gray-500 border focus:outline-none w-56"
            style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
          />
        </div>

        {/* Map */}
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#1e3a5f' }}>
          <div ref={mapRef} style={{ height: 460, width: '100%', backgroundColor: '#0b1622' }} />
          <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-2.5 border-t" style={{ borderColor: '#1e3a5f', backgroundColor: '#142236' }}>
            <span className="text-xs text-gray-400">
              <span className="inline-block w-2 h-2 rounded-full align-middle mr-2" style={{ background: region ? hexFor(region) : '#8a949a' }} />
              Showing <span className="text-white font-bold">{region ? titleCase(region) : 'all regions'}</span>
            </span>
            {filtering && (
              <button onClick={reset} className="text-xs font-bold rounded px-3 py-1 border hover:border-gold-500 transition" style={{ color: '#c9a84c', borderColor: '#1e3a5f' }}>
                Reset view &amp; filters
              </button>
            )}
          </div>
        </div>

        {/* Regions + People */}
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5 items-start">
          <div className="border rounded-xl overflow-hidden" style={{ borderColor: '#1e3a5f', backgroundColor: '#0d1b2e' }}>
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: '#1e3a5f' }}>
              <h2 className="text-xs font-bold text-gray-300 uppercase tracking-widest">Regions</h2>
              <span className="text-[10px] text-gray-600 uppercase tracking-wide">click to filter</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                <RegionRow label="All regions" count={people.length} active={region === null} onClick={() => selectRegion(null)} />
                {regions.map((r) => (
                  <RegionRow key={r} label={titleCase(r)} hex={hexFor(r)} count={people.filter((p) => p.region === r).length} active={region === r} onClick={() => selectRegion(r)} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="border rounded-xl overflow-hidden" style={{ borderColor: '#1e3a5f', backgroundColor: '#0d1b2e' }}>
            <div className="px-4 py-3 border-b flex items-center gap-3 flex-wrap" style={{ borderColor: '#1e3a5f' }}>
              <select
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="rounded px-3 py-2 text-sm border focus:outline-none flex-1 min-w-[180px]"
                style={{ backgroundColor: '#142236', borderColor: '#1e3a5f', color: '#c9a84c' }}
              >
                <option value="ALL">All titles ({people.length})</option>
                {titles.list.map((t) => <option key={t} value={t}>{titleCase(t)} ({titles.counts[t]})</option>)}
              </select>
              <span className="text-xs text-gray-500">{filtered.length} {filtered.length === 1 ? 'person' : 'people'}</span>
            </div>
            <div className="max-h-[540px] overflow-y-auto">
              {loading ? (
                <div className="p-10 text-center text-gray-500">Loading directory…</div>
              ) : filtered.length === 0 ? (
                <div className="p-10 text-center text-gray-500"><b className="block text-white mb-1">No matches</b>Try a different region or title.</div>
              ) : filtered.map(({ p, i }) => {
                const hex = hexFor(p.region);
                return (
                  <button key={i} onClick={() => setSelected(p)} className="w-full flex items-center gap-3 px-4 py-3 border-b text-left hover:bg-white/5 transition" style={{ borderColor: '#13212f' }}>
                    <span style={{ width: 4, alignSelf: 'stretch', borderRadius: 3, background: hex }} />
                    <span className="grid place-items-center text-white font-bold text-sm shrink-0" style={{ width: 38, height: 38, borderRadius: 9, background: hex }}>{initials(p.name)}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-white font-semibold text-sm truncate">{p.name}</span>
                      <span className="block text-gray-400 text-xs truncate">{titleCase(p.title)}</span>
                    </span>
                    <span className="text-[11px] text-gray-500 shrink-0 text-right font-mono">{p.office || ''}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {selected && <ContactCard person={selected} onClose={() => setSelected(null)} />}
    </Layout>
  );
}
